import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import {
  Headset,
  Zap,
  Monitor,
  Printer,
  Wifi,
  Mail,
  HardDrive,
  Users,
  Clock,
  Shield,
  CheckCircle2,
  ArrowRight,
  Building2,
  Warehouse,
  Store,
  Briefcase,
  MessageSquare,
  Phone,
  AlertCircle,
  Send,
  Loader2,
  Bot,
  User,
  RefreshCw,
  X,
  ChevronDown,
  Sparkles,
  Image,
  Upload,
  Ticket
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO from "../components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Markdown components defined outside to avoid re-creation on each render
const markdownComponents = {
  p: ({children}) => <p style={{marginBottom: '0.5rem'}}>{children}</p>,
  ul: ({children}) => <ul style={{listStyleType: 'disc', paddingLeft: '1rem', marginBottom: '0.5rem'}}>{children}</ul>,
  ol: ({children}) => <ol style={{listStyleType: 'decimal', paddingLeft: '1rem', marginBottom: '0.5rem'}}>{children}</ol>,
  li: ({children}) => <li style={{marginBottom: '0.25rem'}}>{children}</li>,
  strong: ({children}) => <strong style={{color: '#93c5fd', fontWeight: '600'}}>{children}</strong>
};

// AI IT Support Chat Component
const ITSupportChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [showSetup, setShowSetup] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem("assistProSession");
    if (savedSession) {
      const { sessionId: savedId, customerName: savedName, businessName: savedBusiness } = JSON.parse(savedSession);
      setSessionId(savedId);
      setCustomerName(savedName || "");
      setBusinessName(savedBusiness || "");
      setShowSetup(false);
      // Load history
      loadHistory(savedId);
    }
  }, []);

  const loadHistory = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/assist-pro/history/${sid}`);
      if (response.data.history && response.data.history.length > 0) {
        setMessages(response.data.history.map(msg => ({
          role: msg.role,
          content: msg.content
        })));
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const startSession = () => {
    const newSessionId = `assist-pro-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setShowSetup(false);
    localStorage.setItem("assistProSession", JSON.stringify({
      sessionId: newSessionId,
      customerName,
      businessName
    }));
    
    // Add welcome message
    setMessages([{
      role: "assistant",
      content: `Hello${customerName ? ` ${customerName}` : ""}! I'm your SmartIT Assist Pro virtual IT technician. I'm here to help you with any IT issues you're experiencing.\n\n**I can help with:**\n- Windows problems\n- Microsoft Office issues\n- Email and Outlook\n- Printer setup\n- Wi-Fi and network issues\n- Account access problems\n- Hardware diagnostics\n- **Screenshot/image analysis** - send me a picture of any error!\n- And much more!\n\n📷 **Tip:** You can upload screenshots or photos of error messages and I'll analyze them for you!\n\n**What IT issue can I help you with today?**`
    }]);
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !selectedImage) || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    
    // Add user message with image if present
    const userMsgContent = selectedImage 
      ? { text: userMessage || "Please analyze this image", hasImage: true, imagePreview: imagePreview }
      : userMessage;
    
    setMessages(prev => [...prev, { role: "user", content: userMsgContent }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/assist-pro/chat`, {
        message: userMessage || "Please analyze this image and help me with any IT issues you can identify.",
        session_id: sessionId,
        customer_name: customerName,
        business_name: businessName,
        image_base64: selectedImage
      });

      // Clear image after sending
      clearImage();

      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or contact SmartIT directly at **07435324947** for immediate assistance." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewConversation = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API_URL}/api/assist-pro/history/${sessionId}`);
      } catch (error) {
        console.error("Error clearing history:", error);
      }
    }
    const newSessionId = `assist-pro-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    localStorage.setItem("assistProSession", JSON.stringify({
      sessionId: newSessionId,
      customerName,
      businessName
    }));
    setMessages([{
      role: "assistant",
      content: `Hello${customerName ? ` ${customerName}` : ""}! Starting a fresh conversation. What IT issue can I help you with today?`
    }]);
  };

  // Quick action buttons
  const quickActions = [
    { label: "My computer is slow", icon: Monitor },
    { label: "Email not working", icon: Mail },
    { label: "Printer issues", icon: Printer },
    { label: "Wi-Fi problems", icon: Wifi },
    { label: "Software error", icon: AlertCircle },
    { label: "Password help", icon: Shield }
  ];

  if (showSetup) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Start IT Support Session</h3>
          <p className="text-slate-400">Let me know a bit about you so I can provide personalized support</p>
        </div>

        <div className="space-y-4 max-w-md mx-auto">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="John Smith"
              className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              data-testid="assist-pro-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Business Name (Optional)</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="ABC Company Ltd"
              className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              data-testid="assist-pro-business-input"
            />
          </div>
          <button
            onClick={startSession}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold py-4 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 mt-6"
            data-testid="assist-pro-start-session-btn"
          >
            <Sparkles className="w-5 h-5" />
            Start Support Session
          </button>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Your session is private and secure. All data is protected.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden" data-testid="assist-pro-chat">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">SmartIT Assist Pro</h3>
            <p className="text-white/70 text-sm">Virtual IT Technician</p>
          </div>
        </div>
        <button
          onClick={startNewConversation}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          title="Start new conversation"
          data-testid="assist-pro-new-chat-btn"
        >
          <RefreshCw className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages Container */}
      <div className="h-[500px] overflow-y-auto p-4 space-y-4" data-testid="assist-pro-messages">
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                message.role === "user" 
                  ? "bg-blue-500" 
                  : "bg-gradient-to-br from-blue-500 to-cyan-500"
              }`}>
                {message.role === "user" ? (
                  <User className="w-4 h-4 text-white" />
                ) : (
                  <Bot className="w-4 h-4 text-white" />
                )}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === "user"
                  ? "bg-blue-500 text-white"
                  : "bg-slate-700/50 text-slate-200"
              }`}>
                {message.role === "assistant" ? (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown components={markdownComponents}>
                      {message.content.replace(/\[OPEN_TICKET\]/g, '').replace(/\[CONTACT_WHATSAPP\]/g, '')}
                    </ReactMarkdown>
                    {/* Escalation buttons if message contains escalation markers */}
                    {(message.content.includes('[OPEN_TICKET]') || message.content.includes('[CONTACT_WHATSAPP]')) && (
                      <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <Link
                          to="/register"
                          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Ticket className="w-4 h-4" />
                          Open Support Ticket
                        </Link>
                        <a
                          href="https://wa.me/447435324947?text=Hi%20SmartIT%2C%20I%20need%20help%20with%20an%20IT%20issue%20that%20Assist%20Pro%20couldn%27t%20resolve."
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <MessageSquare className="w-4 h-4" />
                          WhatsApp SmartIT
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {/* User message - check if it has an image */}
                    {typeof message.content === 'object' && message.content.hasImage ? (
                      <div>
                        {message.content.text && <p className="mb-2">{message.content.text}</p>}
                        <img 
                          src={message.content.imagePreview} 
                          alt="Uploaded" 
                          className="max-w-full rounded-lg max-h-48 object-contain"
                        />
                      </div>
                    ) : (
                      <p>{typeof message.content === 'object' ? message.content.text : message.content}</p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-slate-700/50 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                <span className="text-slate-400 text-sm">Analyzing your issue...</span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions (show only when few messages) */}
      {messages.length <= 1 && (
        <div className="px-4 pb-4">
          <p className="text-slate-500 text-sm mb-3">Quick actions:</p>
          <div className="flex flex-wrap gap-2">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={() => {
                  setInput(action.label);
                  inputRef.current?.focus();
                }}
                className="flex items-center gap-2 px-3 py-2 bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-300 transition-colors"
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <action.icon className="w-4 h-4 text-blue-400" />
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-slate-700/50">
        {/* Image Preview */}
        {imagePreview && (
          <div className="mb-3 relative inline-block">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-h-32 rounded-lg border border-slate-600"
            />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <div className="flex gap-2">
          {/* Image Upload Button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="assist-pro-image-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded-xl transition-colors"
            title="Upload screenshot or image"
            disabled={isLoading}
            data-testid="assist-pro-upload-btn"
          >
            <Image className="w-5 h-5 text-slate-400" />
          </button>
          
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={selectedImage ? "Describe the issue in this image..." : "Describe your IT issue or upload a screenshot..."}
            rows={1}
            className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
            disabled={isLoading}
            data-testid="assist-pro-message-input"
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && !selectedImage) || isLoading}
            className="px-6 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="assist-pro-send-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-slate-500 text-xs mt-2 text-center">
          📷 Upload screenshots of errors for instant analysis • Press Enter to send
        </p>
      </div>
    </div>
  );
};

const AssistProPage = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Zap,
      title: "Instant Response",
      description: "Get immediate help for any IT issue - no waiting, no queues"
    },
    {
      icon: Monitor,
      title: "Fast Diagnostics",
      description: "Windows, Office, email, printers, Wi-Fi, accounts, hardware"
    },
    {
      icon: CheckCircle2,
      title: "Step-by-Step Guidance",
      description: "Clear troubleshooting instructions tailored to your issue"
    },
    {
      icon: Clock,
      title: "First Come, First Served",
      description: "Support handled in order of arrival - fair and efficient"
    },
    {
      icon: Wifi,
      title: "Remote Fixes",
      description: "Most issues resolved remotely without disruption"
    },
    {
      icon: Users,
      title: "On-Site Intervention",
      description: "If remote fix is not possible, we come to you"
    },
    {
      icon: AlertCircle,
      title: "Auto Escalation",
      description: "Complex issues automatically escalated to SmartIT technician"
    },
    {
      icon: Shield,
      title: "Fixed Monthly Price",
      description: "No surprises - one simple price covers everything"
    }
  ];

  const diagnosticAreas = [
    { icon: Monitor, name: "Windows Issues" },
    { icon: Mail, name: "Email Problems" },
    { icon: Printer, name: "Printer Setup" },
    { icon: Wifi, name: "Wi-Fi & Network" },
    { icon: HardDrive, name: "Hardware Faults" },
    { icon: Users, name: "Account Access" }
  ];

  const idealFor = [
    { icon: Building2, name: "Small Businesses", description: "1-20 employees" },
    { icon: Store, name: "Retail & Salons", description: "Customer-facing shops" },
    { icon: Warehouse, name: "Warehouses", description: "Logistics & stock" },
    { icon: Briefcase, name: "Local Companies", description: "Any industry" }
  ];

  return (
    <MainLayout>
      <SEO 
        title="SmartIT Assist Pro - AI IT Support £40/month | Virtual IT Technician"
        description="Dedicated AI-powered virtual IT technician for small businesses. Instant response, fast diagnostics, remote fixes, and on-site support when needed. One fixed price: £40/month."
        keywords="AI IT support, virtual IT technician, small business IT support, monthly IT support, remote IT help, Aylesbury IT support"
        url="https://thesmartit.co.uk/assist-pro"
      />

      {/* Hero Section */}
      <section className="pt-12 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-slate-950 to-cyan-600/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Bot className="w-5 h-5 text-blue-400" />
              <span className="text-blue-300 font-medium">AI-Powered IT Support</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Smart<span className="text-blue-500">IT</span> Assist Pro
            </h1>
            
            <p className="text-xl sm:text-2xl text-slate-300 mb-4 max-w-3xl mx-auto">
              Your AI Virtual IT Technician
            </p>
            
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-6">
              Get instant IT support with our AI assistant. Step-by-step troubleshooting, 
              expert guidance, and automatic escalation to human support when needed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Chat Section - Main Feature */}
      <section className="py-8 relative z-10" id="support-chat">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              <Sparkles className="w-6 h-6 inline-block mr-2 text-cyan-400" />
              Get IT Help Now
            </h2>
            <p className="text-slate-400">
              Describe your IT problem and get instant step-by-step guidance
            </p>
          </motion.div>

          <ITSupportChat />
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What is Included
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Everything you need to keep your business running smoothly
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/30 transition-colors"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diagnostic Areas */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Fast Diagnostics For
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We handle all common IT issues your business might face
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {diagnosticAreas.map((area, index) => (
              <motion.div
                key={area.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-xl p-4 text-center hover:border-blue-500/30 transition-colors"
              >
                <area.icon className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">{area.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Describe Your Issue",
                description: "Tell our AI assistant about your IT problem. Be as detailed as possible for the best help."
              },
              {
                step: "2",
                title: "Get Step-by-Step Help",
                description: "Receive instant, personalized troubleshooting guidance. Follow the steps to resolve your issue."
              },
              {
                step: "3",
                title: "Escalation If Needed",
                description: "If the AI cannot resolve it, the issue is automatically escalated to a SmartIT technician for remote or on-site help."
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-slate-600" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ideal For
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Perfect for small businesses that need reliable IT support without the cost of a full-time IT team
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {idealFor.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6 text-center"
              >
                <item.icon className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-1">{item.name}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-600/20 via-slate-900 to-cyan-600/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Need More Help?
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              If the AI assistant cannot resolve your issue, contact SmartIT directly for remote or on-site support.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/447435324947?text=Hi%20SmartIT%2C%20I%20need%20IT%20support.%20Can%20you%20help%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity text-lg"
                data-testid="assist-pro-cta-subscribe"
              >
                <MessageSquare className="w-5 h-5" />
                Contact via WhatsApp
              </a>
              <a
                href="#support-chat"
                className="inline-flex items-center justify-center gap-2 bg-slate-700 border border-slate-600 text-white font-semibold px-8 py-4 rounded-xl hover:bg-slate-600 transition-colors text-lg"
                data-testid="assist-pro-try-now"
              >
                <Bot className="w-5 h-5" />
                Try AI Support Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </MainLayout>
  );
};

export default AssistProPage;
